import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from "rxjs/operators";
import { throwError } from "rxjs";
import { Store } from "@ngrx/store";

import { MediaService } from "../services";
import {
	DeleteMedia,
	LoadMedias,
	MediaActionTypes,
	MediasOfTicketRequested,
	MediasOfTreatmentRequested,
	UpdateMedia,
} from "./media.actions";
import { MediaModel } from "../models/media.model";
import { AppState } from "libs/tm-common/src/lib/store/reducers/reducers";
import { TreatmentRequested } from "libs/treatments/src/lib/store";

@Injectable({
	providedIn: "root",
})
export class MediaEffects {
	private actions = inject(Actions);
	private store = inject<Store<AppState>>(Store);
	private mediaService = inject(MediaService);

	medias$ = createEffect(() =>
		this.actions.pipe(
			ofType<MediasOfTreatmentRequested>(MediaActionTypes.MediasOfTreatmentRequested),
			withLatestFrom(this.store),
			mergeMap(([action, store]) => {
				const treatmentId = action.payload?.treatmentId
					? action.payload.treatmentId
					: store.router.state.params.id;
				const fetchFromAssociatedTreatments = action.payload?.fetchFromAssociatedTreatments ?? false;
				return this.mediaService.getMediaFiles(treatmentId, fetchFromAssociatedTreatments);
			}),
			map((medias: MediaModel[]) => new LoadMedias({ medias })),
			catchError((err) => {
				console.error("error loading mediafiles");
				return throwError(() => new Error(err));
			})
		)
	);

	mediasByTicketId$ = createEffect(() =>
		this.actions.pipe(
			ofType<MediasOfTicketRequested>(MediaActionTypes.MediasOfTicketRequested),
			switchMap((action) => {
				return this.mediaService.getMediaFilesByTicketId(action.payload.ticketId);
			}),
			map((medias: MediaModel[]) => new LoadMedias({ medias })),
			catchError((err) => {
				console.error("error loading mediafiles by ticket id");
				return throwError(() => new Error(err));
			})
		)
	);

	updateMedia$ = createEffect(
		() =>
			this.actions.pipe(
				ofType<UpdateMedia>(MediaActionTypes.UpdateMedia),
				map((action) =>
					this.mediaService.updateMedium(
						action.payload.media.id as string,
						action.payload.media.changes
					)
				),
				catchError((err) => {
					console.error("error updating mediafile");
					return throwError(() => new Error(err));
				})
			),
		{ dispatch: false }
	);

	deletedMedias$ = createEffect(() =>
		this.actions.pipe(
			ofType<DeleteMedia>(MediaActionTypes.DeleteMedia),
			mergeMap((action) =>
				this.mediaService.deleteFile(action.payload.id).pipe(
					take(1),
					map(() => {
						if (action.payload.treatmentId) {
							return new TreatmentRequested({ id: action.payload.treatmentId });
						}
					}),
					filter((action) => !!action),
					catchError((err) => {
						console.error("Error deleting mediafile:", err);
						return throwError(() => new Error(err));
					})
				)
			)
		)
	);
}
